import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { CapTableVersion } from 'common/types/company';
import {
  PrimaryNavigationMenu,
  PrimaryNavigationMenuAction,
  PrimaryNavigationMenuItem,
  PrimaryNavigationMenuItemLeadingAction,
  PrimaryNavigationMenuSearch,
  PrimaryNavigationMenuShowMoreItem,
  SecondaryNavigationSelector,
} from 'components/ui';
import { useSearchableLimitedOptions } from 'components/ui/hooks';
import { useZoom } from 'context/ZoomContext';
import { toString } from 'utilities';

const CAP_TABLE_VERSION_MENU_VISIBLE_ITEM_LIMIT = 8;
const CAP_TABLE_VERSION_SELECTOR_TEST_ID = 'secondary-navigation__selector-cap-table-version-name';
const CAP_TABLE_SAVE_AS_NEW_PROFORMA_TEST_ID = 'secondary-navigation__selector-cap-table-save-as-new-proforma';
const CAP_TABLE_VERSION_SELECTOR_MAX_WIDTH = 320;

interface CapTableVersionOption {
  companyName: string;
  showCompanyName: boolean;
  version: CapTableVersion;
}

interface CapTableVersionSelectorProps {
  capTableVersionsByCompany: Record<string, CapTableVersion[]>;
  disabled?: boolean;
  onEditVersion?: () => void;
  onSaveAsNewProforma?: () => void;
  onVersionSelect: (slug: string) => void;
  selectedVersion: string | number | null | undefined;
  tooltipTitle?: ReactNode;
}

const CapTableVersionSelector = ({
  capTableVersionsByCompany,
  disabled = false,
  onEditVersion,
  onSaveAsNewProforma,
  onVersionSelect,
  selectedVersion,
  tooltipTitle,
}: CapTableVersionSelectorProps) => {
  const { zoomLevel } = useZoom();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [editIconHoveredVersionId, setEditIconHoveredVersionId] = useState<string | null>(null);
  const [positiveFeedbackKey, setPositiveFeedbackKey] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const entries = useMemo(() => Object.entries(capTableVersionsByCompany), [capTableVersionsByCompany]);
  const showCompanyNames = entries.length > 1;
  const versionOptions = useMemo(
    () => entries.flatMap(([companyName, versions]) => versions.map(version => ({
      companyName,
      showCompanyName: showCompanyNames,
      version,
    }))),
    [entries, showCompanyNames]
  );
  const {
    canShowFewerOptions,
    hasMoreOptions,
    hiddenOptionCount,
    resetSearchableOptions,
    searchValue,
    setSearchValue,
    setShowAllOptions,
    visibleOptions,
  } = useSearchableLimitedOptions<CapTableVersionOption>({
    getSearchText: option => `${option.companyName} ${option.version.name}`,
    options: versionOptions,
    visibleLimit: CAP_TABLE_VERSION_MENU_VISIBLE_ITEM_LIMIT,
  });

  const handleOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (disabled) {
        return;
      }

      setAnchorEl(event.currentTarget);
    },
    [disabled]
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    resetSearchableOptions();
  }, [resetSearchableOptions]);

  // Restricted tabs can disable this selector while the menu is open; close stale anchors.
  useEffect(() => {
    if (disabled) {
      setAnchorEl(null);
    }
  }, [disabled]);

  const handleSelect = useCallback(
    (slug: string) => {
      const selectedCapTableVersion = versionOptions.find(option => option.version.slug === slug)?.version;

      handleClose();
      if (toString(selectedCapTableVersion?.id) !== toString(selectedVersion)) {
        setPositiveFeedbackKey(key => key + 1);
      }
      onVersionSelect(slug);
    },
    [handleClose, onVersionSelect, selectedVersion, versionOptions]
  );

  const handleEditVersion = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      handleClose();
      onEditVersion?.();
    },
    [handleClose, onEditVersion]
  );

  const selectedVersionName = useMemo(() => {
    const found = versionOptions.find(option => toString(option.version.id) === toString(selectedVersion))?.version;
    return found?.name ?? '';
  }, [selectedVersion, versionOptions]);

  const handleSaveAsNewProforma = useCallback(() => {
    handleClose();
    onSaveAsNewProforma?.();
  }, [handleClose, onSaveAsNewProforma]);

  if (versionOptions.length === 0) {
    return null;
  }

  return (
    <>
      <SecondaryNavigationSelector
        aria-expanded={Boolean(anchorEl) && !disabled}
        aria-haspopup="menu"
        data-testid={CAP_TABLE_VERSION_SELECTOR_TEST_ID}
        disabled={disabled}
        label="Cap Table Version"
        onClick={handleOpen}
        positiveFeedbackKey={positiveFeedbackKey}
        sx={{ maxWidth: CAP_TABLE_VERSION_SELECTOR_MAX_WIDTH }}
        tooltipTitle={tooltipTitle}
        value={selectedVersionName}
      />
      <PrimaryNavigationMenu
        anchorEl={anchorEl}
        disableAutoFocusItem
        horizontalPlacement="right"
        menuMaxWidth={320}
        menuMinWidth={180}
        menuWidth="content"
        onClose={handleClose}
        open={Boolean(anchorEl) && !disabled}
        scrollHintItemLabelPlural="versions"
        scrollHintItemLabelSingular="version"
        zoomLevel={zoomLevel}
        TransitionProps={{ onEntered: () => searchInputRef.current?.focus() }}
        MenuListProps={{ disableListWrap: true }}>
        <PrimaryNavigationMenuSearch
          clearAriaLabel="Clear cap table version search"
          fullWidth
          inputRef={searchInputRef}
          onChange={event => setSearchValue(event.target.value)}
          onClear={() => setSearchValue('')}
          placeholder="Find a Version"
          value={searchValue}
        />
        {visibleOptions.map(({ companyName, showCompanyName, version }) => {
          const isSelected = toString(version.id) === toString(selectedVersion);
          const versionId = toString(version.id);

          return (
            <PrimaryNavigationMenuItem
              data-testid={`secondary-navigation__selector-cap-table-version-option-${versionId}`}
              endIcon={isSelected && onEditVersion ? (
                <PrimaryNavigationMenuItemLeadingAction
                  aria-label="Edit cap table version"
                  isVisible
                  onClick={handleEditVersion}
                  onMouseEnter={() => setEditIconHoveredVersionId(versionId)}
                  onMouseLeave={() => setEditIconHoveredVersionId(null)}
                  style={{
                    backgroundColor: editIconHoveredVersionId === versionId ? 'rgba(0,0,0,0.1)' : undefined,
                    borderRadius: 6,
                    height: 20,
                    padding: 2,
                    transition: 'background-color 120ms ease',
                    width: 20,
                  }}>
                  <EditOutlinedIcon fontSize="inherit" />
                </PrimaryNavigationMenuItemLeadingAction>
              ) : undefined}
              isActive={isSelected}
              key={`${companyName}-${version.id}`}
              onClick={() => handleSelect(version.slug ?? '')}>
              {showCompanyName ? `${companyName} · ${version.name}` : version.name}
            </PrimaryNavigationMenuItem>
          );
        })}
        {visibleOptions.length === 0 && (
          <PrimaryNavigationMenuItem disabled tone="muted">
            No versions found
          </PrimaryNavigationMenuItem>
        )}
        {hasMoreOptions && (
          <PrimaryNavigationMenuShowMoreItem
            count={hiddenOptionCount}
            itemLabelPlural="versions"
            itemLabelSingular="version"
            onClick={() => setShowAllOptions(true)}
            variant="more"
          />
        )}
        {canShowFewerOptions && (
          <PrimaryNavigationMenuShowMoreItem
            itemLabelPlural="versions"
            itemLabelSingular="version"
            onClick={() => setShowAllOptions(false)}
            variant="fewer"
          />
        )}
        {onSaveAsNewProforma && (
          <PrimaryNavigationMenuAction
            data-testid={CAP_TABLE_SAVE_AS_NEW_PROFORMA_TEST_ID}
            icon={<AddIcon />}
            onClick={handleSaveAsNewProforma}>
            Save as New Proforma
          </PrimaryNavigationMenuAction>
        )}
      </PrimaryNavigationMenu>
    </>
  );
};

export default CapTableVersionSelector;
